import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import {
  AdminWidgetTitle,
  AdminMainText,
  AdminSupportingText,
  AdminMetadataText,
  AdminMetric,
  AdminStatusBadge,
  AdminHeaderAction,
  AdminLinkAction,
} from "../../../sections/AdminDashboard/shared/adminUx";
import { AdminWidgetHeader } from "../../../components/BusinessDashboard/AdminWidgetChrome";
import PaymentHistory from "../../../sections/BusinessDashboard/Subscriptions/Payment/PaymentHistory";
import AddNew from "../../../sections/BusinessDashboard/Subscriptions/Payment/AddNew";
import UpdateCardPopup from "../../../components/BusinessDashboard/UpdateCardPopup";
import CancelSubscriptionPopup from "../../../components/BusinessDashboard/CancelSubscriptionPopup";
import card from "../../../assets/img/payment/master-card-dark.jpeg";
import Masonry from "masonry-layout";
import * as echarts from 'echarts';
import useAuth from "../../../Hooks/useAuth";
import { BUSINESS_SUBSCRIPTION_CONTENT_CLASS } from "../../../sections/BusinessDashboard/shared/businessLayoutSpec";

const PaymentDetails = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [autoRenew, setAutoRenew] = useState(true);
  const [isUpdateCardPopupOpen, setIsUpdateCardPopupOpen] = useState(false);
  const [isCancelSubscriptionPopupOpen, setIsCancelSubscriptionPopupOpen] = useState(false);

  // Check if user is on basic plan
  const isBasicPlan = auth?.userPlan?.plan?.name?.toLowerCase() === 'basic';

  // Mock data for the widgets-only UI. Replace with real data when wiring API.
  const subscription = {
    plan: "Basic Plan",
    status: "Active",
    renewsOn: "25 July 2025",
    type: "Monthly",
    nextPayment: "$49.95",
    nextPaymentDate: "25 July 2025",
    subscribedSince: "Mar 2023",
    totalPaid: "$1,198.80",
  };

  const comparePlans = {
    headers: ["Basic", "Pro"],
    rows: [
      { label: "Post Type Access", values: ["Regular", "Premium"] },
      { label: "Max Listings", values: ["Unlimited", "Unlimited"] },
      { label: "Featured Post Slots", values: ["02", "10"] },
      { label: "Keyword Limit", values: ["100", "100"] },
      { label: "Categories", values: ["Standard", "Priority"] },
      { label: "Priority in Quote Access", values: ["$49.95", "$89.95"] },
    ],
  };

  const chart = useMemo(() => {
    // Simplified sparkline-like points across months
    return {
      months: ["Mar", "Apr", "May", "Jun", "Jul"],
      // sample monthly payment amounts (for tooltip display)
      points: [0, 19.10, 29.35, 39.00, 49.85],
      lastValue: "$49.85",
    };
  }, []);

  // Masonry setup
  const masonryGridRef = useRef(null);
  useEffect(() => {
    if (masonryGridRef.current) {
      try {
        const images = masonryGridRef.current.querySelectorAll('img');
        const imagePromises = Array.from(images).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        });
        Promise.all(imagePromises).then(() => {
          if (masonryGridRef.current && typeof Masonry !== 'undefined') {
            const masonry = new Masonry(masonryGridRef.current, {
              itemSelector: '.masonry-item',
              columnWidth: 10,
              gutter: 0,
            });
            const handleResize = () => masonry && masonry.layout && masonry.layout();
            window.addEventListener('resize', handleResize);
            if (masonryGridRef.current) {
              masonryGridRef.current.masonry = masonry;
            }
            return () => {
              window.removeEventListener('resize', handleResize);
              if (masonryGridRef.current?.masonry) {
                masonryGridRef.current.masonry.destroy();
              }
            };
          }
        });
      } catch (e) {
        console.warn('Masonry failed to init on payments page:', e);
      }
    }
  }, []);

  // (Single masonry grid used for all widgets)

  // ECharts: refs and effects for Payment History Overview
  const paymentHistoryChartRef = useRef(null);
  useEffect(() => {
    if (!paymentHistoryChartRef.current) return;
    const chartInstance = echarts.init(paymentHistoryChartRef.current);
    const dataPoints = chartRefPoints();
    const option = {
      grid: { left: 0, right: 0, top: 0, bottom: 18 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#FFFFFF',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: '#232A2E', fontSize: 11, fontWeight: 500, fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' },
        axisPointer: { type: 'line', lineStyle: { color: '#A78BFA', width: 1 } },
        formatter: function (params) {
          const p = Array.isArray(params) ? params[0] : params;
          // const label = String(p.axisValue).slice(0,3).toLowerCase();
          // const month = label.charAt(0).toUpperCase() + label.slice(1);
          const value = typeof p.data === 'number' ? p.data.toFixed(2) : p.data;
          return '$' + value;
        }
      },
      xAxis: {
        type: 'category',
        data: chart.months,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { 
          show: true, 
          color: '#6B7280', 
          fontSize: 10, 
          margin: 8, 
          formatter: function(value, index){ 
            // Only show first and last labels (2 labels total)
            if (index === 0 || index === chart.months.length - 1) {
              const s = String(value).slice(0,3).toLowerCase(); 
              return s.charAt(0).toUpperCase() + s.slice(1); 
            }
            return '';
          } 
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false, lineStyle: { color: '#E5E7EB' } },
        min: function (value) { return value.min - 2; },
        max: function (value) { return value.max + 2; }
      },
      series: [{
        // type: 'line',
        data: dataPoints,
        // smooth: true,
        // symbol: 'circle',
        // // symbolSize: function (val, params) { return params.dataIndex === dataPoints.length - 1 ? 8 : 0; },
        // lineStyle: { color: '#A78BFA', width: 3 },
        // itemStyle: { color: '#A78BFA' },
        // emphasis: { focus: 'series' },
        type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#2E00FF', width: 2 },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(123, 98, 217, 0.4)' }] } }
      }]
    };
    chartInstance.setOption(option);
    const handleResize = () => chartInstance.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.dispose();
    };
    // helper to lock points
    function chartRefPoints() { return chartDataPoints; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // stable data points for chart effect
  const chartDataPoints = chart.points;

  return (
    <>
      <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
        <div className={BUSINESS_SUBSCRIPTION_CONTENT_CLASS}>
          <div ref={masonryGridRef} className="masonry-grid">
              {/* Current Subscription widget */}
              <DashboardMainContainer className="masonry-item w-[350px] rounded-xl bg-mistygray overflow-hidden">
                <div className="py-3 px-4 flex items-center justify-between">
                  <div>
                    <AdminWidgetTitle className="text-offblack mb-1 block">{subscription.plan}</AdminWidgetTitle>
                    <AdminMetadataText className="text-offblack block">
                      Renews on {subscription.renewsOn}
                    </AdminMetadataText>
                  </div>
                  <AdminStatusBadge status="Active" label={subscription.status} />
                </div>
                <div className="border-b border-lightgray mx-4"></div>
                <div className="p-4 grid grid-rows-2 gap-2">
                  <div className="flex flex-row items-center justify-between">
                    <AdminMainText className="text-offblack">Subscription Type</AdminMainText>
                    <AdminSupportingText className="text-offblack">{subscription.type}</AdminSupportingText>
                  </div>
                  <div className="flex flex-row items-center justify-between">
                    <AdminMainText className="text-offblack">Next Payment</AdminMainText>
                    <AdminSupportingText className="text-offblack">
                      {subscription.nextPayment}{' '}
                      <AdminMetadataText as="span" className="text-offblack">
                        on {subscription.nextPaymentDate}
                      </AdminMetadataText>
                    </AdminSupportingText>
                  </div>
                </div>
              </DashboardMainContainer>

              {/* Compare Plans */}
              <DashboardMainContainer className="masonry-item w-[450px] h-[373px] rounded-xl bg-mistygray overflow-hidden">
                <AdminWidgetHeader title="Compare Plans" className="border-b-0 pb-1" />
                <div className="p-4 pt-0">
                <div className="w-full border-b border-lightgray relative -bottom-[39px] z-10"></div>
                  <div className="grid grid-cols-[160px_1fr_1fr] gap-4 items-start">
                    {/* Labels */}
                    <div className="py-2">
                      <div className="h-8" />
                      {comparePlans.rows.map((row) => (
                        <div key={row.label} className="py-[6px]">
                          <AdminMainText className="text-offblack">{row.label}</AdminMainText>
                        </div>
                      ))}
                    </div>
                    {/* Combined Plans Background */}
                    <div className="col-span-2 bg-white rounded-xl p-4 py-2">
                      <div className="grid grid-cols-2 gap-x-4">
                        <div className="text-left h-6 mb-2 flex items-center">
                          <AdminSupportingText className="text-offblack">{comparePlans.headers[0]}</AdminSupportingText>
                        </div>
                        <div className="text-left h-6 mb-2 flex items-center">
                          <AdminSupportingText className="text-offblack">{comparePlans.headers[1]}</AdminSupportingText>
                        </div>
                        {comparePlans.rows.map((row) => (
                          <React.Fragment key={row.label + '-vals'}>
                            <div className="text-left py-[6px]">
                              <AdminMainText className="text-offblack">{row.values[0]}</AdminMainText>
                            </div>
                            <div className="text-left py-[6px]">
                              <AdminMainText className="text-offblack">{row.values[1]}</AdminMainText>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                  {isBasicPlan && (
                    <div className="mt-7 flex items-center justify-end">
                      <AdminHeaderAction onClick={() => navigate('/pricing')}>
                        Upgrade
                      </AdminHeaderAction>
                    </div>
                  )}
                </div>
              </DashboardMainContainer>

              {/* Payment History Overview - separate widget */}
              <DashboardMainContainer className="masonry-item w-[350px] rounded-xl bg-mistygray overflow-hidden">
                <AdminWidgetHeader title="Payment History Overview" />
                <div className="px-4 pb-1">
                  <div className="flex justify-start items-center mb-4">
                    <div>
                      <AdminSupportingText className="text-offblack block">Subscribed since</AdminSupportingText>
                      <AdminMetric className="text-offblack">{subscription.subscribedSince}</AdminMetric>
                    </div>
                    <div className="text-left ml-10">
                      <AdminSupportingText className="text-offblack block">Total Paid</AdminSupportingText>
                      <AdminMetric className="text-offblack">{subscription.totalPaid}</AdminMetric>
                    </div>
                  </div>
                  <div className="h-24 relative">
                    <div ref={paymentHistoryChartRef} className="w-full h-full"></div>
                  </div>
                </div>
              </DashboardMainContainer>

            {/* Billing section title as a masonry item */}
            {/* <div className="masonry-item w-[450px]">
              <Heading variant={"h6"} className={"text-left text-offblack my-1"}>
                Billing History
              </Heading>
            </div> */}

              {/* Card image */}
              <DashboardMainContainer className="masonry-item w-[300px] rounded-xl bg-mistygray overflow-hidden ">
                  <img src={card} alt="credit-card" className="w-full h-auto rounded-lg" />
              </DashboardMainContainer>

            {/* Billing widgets inside the same masonry grid */}
            <DashboardMainContainer className="masonry-item w-[500px] rounded-xl bg-mistygray overflow-hidden">
              <div className="px-2 sm:px-4 py-2">
                <PaymentHistory />
              </div>
            </DashboardMainContainer>

              {/* Payment Method */}
              <DashboardMainContainer className="masonry-item w-[300px] rounded-xl bg-mistygray overflow-hidden">
                <AdminWidgetHeader title="Payment Method" />
                <div className="p-4 flex items-center justify-between">
                  <AdminSupportingText className="text-offblack">VISA **** 8541</AdminSupportingText>
                  <AdminSupportingText className="text-offblack bg-mistygray px-3 py-1 rounded">
                    07/27
                  </AdminSupportingText>
                </div>
              </DashboardMainContainer>

            <DashboardMainContainer className="masonry-item w-[500px] rounded-xl bg-mistygray overflow-hidden">
              <div className="divide-y divide-lightgray px-4">
                <div className="flex items-center justify-between py-4">
                  <AdminMainText className="text-offblack">Auto- Renew Subscription</AdminMainText>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoRenew}
                      onChange={() => setAutoRenew((v) => !v)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-lightgray rounded-full peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-lightgray after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primaryblue"></div>
                  </label>
                </div>
                <div className="py-4 flex items-center justify-between">
                  <AdminLinkAction
                    type="button"
                    className="text-offblack"
                    onClick={() => setIsCancelSubscriptionPopupOpen(true)}
                  >
                    Cancel Subscription
                  </AdminLinkAction>
                </div>
              </div>
            </DashboardMainContainer>

            {/* Update Card */}
            <DashboardMainContainer className="masonry-item w-[300px] rounded-xl overflow-hidden !bg-transparent">
              <div className="flex items-center justify-end">
                <AdminHeaderAction
                  className="!w-auto"
                  onClick={() => setIsUpdateCardPopupOpen(true)}
                >
                  <span className="inline-flex items-center gap-2">
                    Update Card
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </AdminHeaderAction>
              </div>
            </DashboardMainContainer>

          </div>
        </div>
      </DashboardMainContainer>

      {/* Update Card Popup */}
      <UpdateCardPopup
        isOpen={isUpdateCardPopupOpen}
        onClose={() => setIsUpdateCardPopupOpen(false)}
      />

      {/* Cancel Subscription Popup */}
      <CancelSubscriptionPopup
        isOpen={isCancelSubscriptionPopupOpen}
        onClose={() => setIsCancelSubscriptionPopupOpen(false)}
        subscriptionData={subscription}
      />
    </>
  );
};

export default PaymentDetails;
